import algoliasearch from 'algoliasearch'
import { ArticleService } from 'api/services/ArticleService'
import { ALGOLIA_INDEX_SUFFIX } from 'env'
import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'
import { ArticleFlat, Auth, MentionableArticle } from 'types'
import { editableByFilter, filtersQuery, viewableByFilter } from 'utils/SearchUtils'
import { DefaultItems, ObjectsToLoad } from '../MentionPrompt/MentionPrompt'

interface UseArticleProps {
  algoliaClient: algoliasearch.Client
  auth: Auth | null
  userId: number
  editableDocsOnly: boolean
  mentioningArticle?: ArticleFlat
  objectsToLoad: ObjectsToLoad[]
  query: string
  numberOfHits?: number
  suggestRecentlyReadArticle?: boolean
  availableForReview?: boolean
  disabled?: boolean
}

export const useArticles = ({
  algoliaClient,
  auth,
  userId,
  editableDocsOnly,
  mentioningArticle,
  objectsToLoad,
  numberOfHits,
  query,
  suggestRecentlyReadArticle,
  availableForReview,
  disabled,
}: UseArticleProps) => {
  const articlesIndex = useMemo(() => algoliaClient.initIndex(`Article_${ALGOLIA_INDEX_SUFFIX}`), [algoliaClient])
  const prevData = useRef<MentionableArticle[]>([])
  const getFilters = () => {
    const companyId = auth?.company_id || mentioningArticle?.company_id
    const showProjects = objectsToLoad.includes(ObjectsToLoad.Projects)
    const showArticles = objectsToLoad.includes(ObjectsToLoad.Articles)
    const projectFilter: string[] = showProjects && showArticles ? [] : [`project_index = ${showProjects ? 1 : 0}`]
    const baseFilters = [`company:${companyId}`, ...projectFilter]
    const authFilter: string = userId ? `made_public:true` : viewableByFilter(companyId, auth)
    const editAccessFilter = editableDocsOnly ? editableByFilter(companyId, auth) : ''
    const availableForReviewFilter = availableForReview ? ['informal_feedback_state = 0', 'locked = 0'] : []
    return [...baseFilters, authFilter, editAccessFilter, ...availableForReviewFilter]
  }

  useEffect(() => {
    if (disabled) return
    // Ping algolia when the component mounts to get SSL handshake out of the way
    articlesIndex.search({
      query: '',
      hitsPerPage: numberOfHits || DefaultItems,
      filters: filtersQuery('AND', getFilters()),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled])

  return useQuery<MentionableArticle[]>(
    [
      'mention-prompt',
      'articles',
      {
        auth: auth?.id,
        userId,
        editableDocsOnly,
        mentioningArticleId: mentioningArticle?.id,
        companyId: auth?.company_id || mentioningArticle?.company_id,
        objectsToLoad,
        query,
      },
    ],
    async () => {
      if (!auth && !userId) {
        return []
      }

      if (!query) {
        if (suggestRecentlyReadArticle && mentioningArticle?.id && auth?.id) {
          const response = await ArticleService.fetchRecentlyReadArticles(mentioningArticle.id)
          return response.data.articles || []
        }
        return []
      }

      const results = await articlesIndex.search({
        query,
        hitsPerPage: numberOfHits || DefaultItems,
        filters: filtersQuery('AND', getFilters()),
      })

      return results.hits || []
    },
    {
      enabled:
        !disabled && (objectsToLoad.includes(ObjectsToLoad.Articles) || objectsToLoad.includes(ObjectsToLoad.Projects)),
      onSuccess: (data) => {
        prevData.current = data
      },
      placeholderData: prevData.current,
      staleTime: 1000 * 60, // 1 minute
    },
  )
}
